import { Link } from "react-router-dom";
import { Facebook, Mail, MapPin, ArrowUpRight } from "lucide-react";
import { useSiteContent } from "@/context/ContentContext";

export default function Footer() {
    const { CONTACT_EMAIL, SOCIALS, LOCATION } = useSiteContent();
    return (
        <footer data-testid="site-footer" className="relative border-t border-white/10 bg-void">
            <div className="bg-scanlines absolute inset-0 pointer-events-none" />
            <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10">
                <div className="grid gap-14 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4">
                            <img src="/assets/camarca-logo.png" alt="Camarca Digital Designs logo" className="h-14 w-14 rounded-full" />
                            <div>
                                <p className="font-display text-lg font-bold text-white">CAMARCA</p>
                                <p className="font-mono text-[10px] tracking-[0.35em] text-zinc-500">DIGITAL DESIGNS</p>
                            </div>
                        </div>
                        <p className="mt-6 max-w-sm text-sm leading-relaxed text-zinc-400">
                            Websites, logos, and branding that make businesses look real. Based in Cincinnati, Ohio — working with clients nationwide.
                        </p>
                        <div className="mt-8 flex gap-3">
                            <a
                                href={SOCIALS.facebook}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="footer-facebook-link"
                                aria-label="Camarca Digital Designs on Facebook"
                                className="flex h-11 w-11 items-center justify-center border border-white/15 text-zinc-400 transition-[color,border-color,box-shadow] duration-300 hover:border-neon hover:text-neon hover:shadow-[0_0_15px_rgba(0,240,255,0.3)]"
                            >
                                <Facebook className="h-4 w-4" />
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">Site Map</p>
                        <ul className="mt-6 space-y-3">
                            {[
                                ["Home", "/"],
                                ["Services", "/services"],
                                ["Work", "/work"],
                                ["Pricing", "/pricing"],
                                ["FAQ", "/faq"],
                                ["Contact", "/contact"],
                                ["Terms", "/terms"],
                            ].map(([label, to]) => (
                                <li key={to}>
                                    <Link
                                        to={to}
                                        data-testid={`footer-link-${label.toLowerCase().replace(/\s/g, "-")}`}
                                        className="group inline-flex items-center gap-1 text-sm text-zinc-400 transition-colors duration-300 hover:text-neon"
                                    >
                                        {label}
                                        <ArrowUpRight className="h-3 w-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-4">
                        <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">Get In Touch</p>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            data-testid="footer-email-link"
                            className="mt-6 flex items-start gap-3 text-sm text-zinc-300 transition-colors duration-300 hover:text-neon"
                        >
                            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                            {CONTACT_EMAIL}
                        </a>
                        <p className="mt-4 flex items-start gap-3 text-sm text-zinc-400">
                            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-neon" />
                            {LOCATION}
                        </p>
                        <p className="mt-6 font-mono text-xs leading-relaxed text-zinc-600">
                            Websites • Logos • Branding • Updates
                        </p>
                    </div>
                </div>

                <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between">
                    <p className="font-mono text-xs text-zinc-600">
                        © {new Date().getFullYear()} Camarca Digital Designs, a Brickhouse One LLC company. Cincinnati, Ohio.
                    </p>
                    <div className="flex items-center gap-6">
                        <p className="font-mono text-xs text-zinc-600">Web design for businesses of any size — local & nationwide.</p>
                        <Link to="/admin" data-testid="footer-admin-link" className="font-mono text-xs text-zinc-700 transition-colors duration-300 hover:text-neon">Admin</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
